<template>
  <transition name="slide-fade">
    <div
      v-if="!dismiss"
      class="alert c-alert"
      :class="alertClass"
    >
      <div class="c-alert__text">{{ alertText }}</div>
      <div class="c-alert__actions">
        <div
          v-if="isNaN(alertDisplay)"
          class="btn c-alert__btn"
          :class="'btn-' + alertType"
          @click="dismissAlert()"
        >{{ alertDisplay }}</div>
      </div>
    </div>
  </transition>
</template>

<script>
/**
 * Eine Komponente die globale UI Benachrichtigungen anzeigt.
 * Wird in der Root App eingebunden, Benachrichtigungen werden im VUEX Store gespeichert.
 * Benachrichtigungen können so aus allen Komponenten commitet werden.
 * Kann mit den Hilfsfunktionen in helpers/HandleAlerts gesteuert werden.
 * Beispiel:
 * `import HandleAlerts from '../helpers/HandleAlerts'`,
 * `methods: {showAlert: HandleAlerts.show, ...`,
 * `this.showAlert('Änderungen wurden gespeichert.', 'success');`
 * @displayName BaseAlert
 */
export default {
  name: 'BaseAlert',
  props: {
    /**
     * Der Typ des Alerts - das sind die Bootstrap Alert Klassen
     * @values info, success, danger, warning
     */
    alertType: {
      type: String,
      default: 'info'
    },
    /**
     * Der Text-Inhlat des Alerts. Layout ist nur vorgesehen für kurze Infos.
     */
    alertText: {
      type: String,
      required: true
    },
    /**
     * Anzeigedauer in Millisekunden ODER Text für den Dismiss Button, wenn der Alert manuell geschlossen werden soll
     */
    alertDisplay: {
      type: [Number, String],
      default: 2000
    },
    /**
     * Unique ID aus dem aktuellen Zeitpunkt - automatisch generiert im VUEX Store bei der Alert Erstellung
     */
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      dismiss: false
    }
  },
  computed: {
    alertClass() {
      let alertClassName = 'alert-' + this.alertType;

      return alertClassName
    }
  },
  mounted: function() {
    if (!isNaN(this.alertDisplay)) {
      setTimeout(()=>{
        this.dismissAlert();
      }, this.alertDisplay)
    }
  },
  methods: {
    dismissAlert() {
      this.dismiss = true;

      setTimeout(()=>{
        this.$store.commit('dismissAlert', this.id);
      }, 500) // delay to give time for the leave animation
    }
  }
}
</script>

<style lang="less">
  //bootstrap base
  .alert {
    padding: 15px;
    margin-bottom: 20px;
    border: 1px solid transparent;
    border-radius: 4px;
  }

  .alert-warning {
    color: #81622c;
    border-color: #fadc9f;
    background-color: #fbf0b7;
  }

  .alert-danger {
    background-color: #f2dede;
    border-color: #ebccd1;
    color: #a94442;
  }

  .alert-success {
    background-color: #dff0d8;
    border-color: #d6e9c6;
    color: #3c763d;
  }

  .alert-info {
    background-color: #d9edf7;
    border-color: #bce8f1;
    color: #31708f;
  }

  .c-alert {
    min-width: 300px;
    max-height: 10rem;
    overflow: hidden;
    box-shadow: 0 0 5px fadeOut(#000, 50%);
    margin-bottom: 1em;

    &__container {
      position: fixed;
      top: 131px;
      right: 5px;
      z-index: 10000;
      overflow: hidden;
    }

    &__actions { // no distances as this must work empty too - alignment only
      text-align: right;
    }

    &__btn {
      margin-top: 10px;
      padding: 4px 8px;
    }
  }

  .slide-fade-enter-active {
    transition: all 0.2s ease-out;
  }

  .slide-fade-leave-active {
    transition: all 0.5s linear;
  }

  .slide-fade-enter-from,
  .slide-fade-leave-to {
    transform: translateX(20px);
    opacity: 0;
    max-height: 0;
  }
</style>